import BaseStore from './baseStore'
import Pagination from '@/store/pagination'
import api from '../api/api'
import {
  createLinkElementThenDownloadFile,
  getDownloadFileNameFromHeaders
} from '@/utils/downloadFile'

export default class {
  constructor(baseUrl, paginationUrl) {
    const baseStore = new BaseStore(baseUrl)
    const pagination = new Pagination(paginationUrl)

    const getDefaultState = () => {
      return {
        ...structuredClone(baseStore.state),
        ...structuredClone(pagination.state),
        dialog: false,
        uploadLoading: false,
        importLoading: false,
        importItems: [],
        years: [],
        ignoredReferencePoint: [],
        deleteWarning: false,
        downloadLoading: false,
        exportLoading: false,
        availableReferencePoints: []
      }
    }

    this.state = getDefaultState()

    this.mutations = {
      ...baseStore.mutations,
      ...pagination.mutations,
      RESET_ITEM_STATE(state) {
        state.items = []
        state.dirtyItem = []
      },
      ADD_IMPORT_ITEMS(state, items) {
        state.importItems = items
      },
      ADD_IGNORE_REFERENCE_POINT(state, items) {
        state.ignoredReferencePoint = items
      },
      SET_YEARS(state, items) {
        state.years = items
      },
      SET_AVAILABLE_REFERENCE_POINTS(state, items) {
        state.availableReferencePoints = items
      },
      ADD_AVAILABLE_REFERENCE_POINT(state, item) {
        state.availableReferencePoints.push(item)
      },
      REMOVE_AVAILABLE_REFERENCE_POINT(state, items) {
        items.forEach((item) => {
          const index = state.availableReferencePoints.indexOf(item)
          if (index > -1) {
            state.availableReferencePoints.splice(index, 1)
          }
        })
      },
      EDIT_AVAILABLE_ITEMS_REFERENCE_POINT(state, { oldId, newId }) {
        let index = state.availableReferencePoints.findIndex((item) => item === newId)
        state.availableReferencePoints[index] = oldId
      },
      TOGGLE_UPLOAD_LOADING(state) {
        state.uploadLoading = !state.uploadLoading
      },
      TOGGLE_IMPORT_LOADING(state) {
        state.importLoading = !state.importLoading
      },
      TOGGLE_DOWNLOAD_LOADING(state) {
        state.downloadLoading = !state.downloadLoading
      },
      TOGGLE_EXPORT_LOADING(state) {
        state.exportLoading = !state.exportLoading
      },
      SET_DELETE_WARNING(state, item) {
        state.deleteWarning = item
      },
      REPLACE_AND_ADD_ITEMS(state, createdObjs) {
        createdObjs.forEach((obj, index) => {
          let itemIndex = state.items.findIndex((item) => item.id === obj.id)
          if (itemIndex === -1) {
            state.items.unshift(createdObjs[index])
            state.count += 1
          } else {
            state.items[itemIndex] = createdObjs[index]
          }
        })
      },
      SET_REFERENCE_POINT(state, { objs, newId }) {
        objs.forEach((obj) => (obj.reference_point = newId))
      },
      EDIT_ITEMS_REFERENCE_POINTS(state, { oldId, newId }) {
        let item = state.items.find((item) => item.id === oldId)
        item.id = newId
      },
      SET_IMPORTED_YEARS(state, items) {
        state.importedYears = items
      },
      SET_COUNT(state, item) {
        state.count = item
      }
    }

    this.actions = {
      ...baseStore.actions,
      ...pagination.actions,
      resetItemState({ commit }) {
        commit('RESET_ITEM_STATE')
      },
      resetImportState(context) {
        context.commit('RESET_IMPORT_STATE')
      },
      resetState({ commit }) {
        commit('RESET_STATE')
      },
      async create(context) {
        context.commit('TOGGLE_WRITE_LOADING')
        let newObjs = context.state.items.filter((dirtyItem) => {
          return dirtyItem.reference_point <= 0 && dirtyItem.id > 0
        })
        if (newObjs.length) {
          let response = await api.post(paginationUrl, newObjs)
          let createdObjs = response.data
          const createdReferencePoints = createdObjs.map((item) => item.id)
          context.commit('REMOVE_AVAILABLE_REFERENCE_POINT', createdReferencePoints)
          context.commit('REPLACE_ITEMS', { newObjs, createdObjs })
        }
        context.commit('TOGGLE_WRITE_LOADING')
      },
      async deleteByReferencePoint(context, referencePoint) {
        if (referencePoint.id > 0) {
          let url = baseUrl + `reference_point/${referencePoint.id}/`
          await api.delete(url)
          context.commit('ADD_AVAILABLE_REFERENCE_POINT', referencePoint.id)
        }
      },
      async fetchAvailableReferencePoints(context) {
        let url = `${paginationUrl}available_reference_point/${context.rootGetters['project/selectedProjectId']}/`
        let response = await api.get(url)
        context.commit('SET_AVAILABLE_REFERENCE_POINTS', response['data'])
      },
      async fetchYears(context) {
        let url = `${baseUrl}year_options/${context.rootGetters['project/selectedProjectId']}/`
        let response = await api.get(url)
        context.commit('SET_YEARS', response['data'])
      },
      async downloadTemplate(context) {
        context.commit('TOGGLE_DOWNLOAD_LOADING')
        try {
          const projectId = context.rootGetters['project/selectedProjectId']
          const url = paginationUrl + 'template/' + projectId + '/'
          const { data, headers } = await api.get(url, { responseType: 'blob' })
          const exportFileName = context.getters.getExportFileName
          createLinkElementThenDownloadFile(
            data,
            getDownloadFileNameFromHeaders(headers) || exportFileName
          )
          context.commit('TOGGLE_DOWNLOAD_LOADING')
          return true
        } catch (e) {
          console.log(e)
          context.commit('TOGGLE_DOWNLOAD_LOADING')
          return false
        }
      },
      setReferencePoint(context, { oldId, newId }) {
        let objs = context.getters.getItems.filter((item) => item.reference_point === oldId)
        context.commit('SET_REFERENCE_POINT', { objs, newId })
        context.dispatch('setDirtyOnArray', objs)
        context.commit('EDIT_AVAILABLE_ITEMS_REFERENCE_POINT', { oldId, newId })
        context.commit('EDIT_ITEMS_REFERENCE_POINTS', { oldId, newId })
      },
      async exportData(context) {
        context.commit('TOGGLE_EXPORT_LOADING')
        try {
          const projectId = context.rootGetters['project/selectedProjectId']
          const url = paginationUrl + 'export/' + projectId + '/'
          const { data, headers } = await api.get(url, { responseType: 'blob' })
          const exportFileName = context.getters.getExportFileName
          createLinkElementThenDownloadFile(
            data,
            getDownloadFileNameFromHeaders(headers) || exportFileName
          )
          context.commit('TOGGLE_EXPORT_LOADING')
          return true
        } catch (e) {
          console.log(e)
          context.commit('TOGGLE_EXPORT_LOADING')
          return false
        }
      },
      async import(context) {
        context.commit('TOGGLE_IMPORT_LOADING')
        if (context.state.importItems.length) {
          const projectId = context.rootGetters['project/selectedProjectId']
          let url = paginationUrl + `import/project_id/${projectId}/`
          try {
            let response = await api.post(url, context.state.importItems)
            context.commit('SET_YEARS', context.state.importedYears)
            context.dispatch('setState')
            let createdObjs = response.data
            const existReferencePoints = createdObjs.map((item) => item.id)
            let referencePoints = await context.rootState['referencePoint'].items
            let availableReferencePoints = []
            referencePoints.features.forEach((ref) => {
              let index = existReferencePoints.findIndex((usedRef) => ref.id === usedRef)
              if (index < 0) {
                availableReferencePoints.push(ref.id)
              }
            })
            context.commit('SET_AVAILABLE_REFERENCE_POINTS', availableReferencePoints)
            context.commit('REPLACE_AND_ADD_ITEMS', createdObjs)
            context.commit('SET_COUNT', context.state.items.length)
            context.commit('RESET_IMPORT_STATE')
          } catch (e) {
            console.log(e)
          }
        }
        context.commit('TOGGLE_IMPORT_LOADING')
      },
      setState() {}
    }

    this.getters = {}
  }
}
